// ============================================================
// heldContact.ts — (batch 2, M17) a half-typed email / phone on the case's
// Client details is HELD BACK by the case autosave (useAutoSave +
// utils/contactFormat holdInvalidContact) until it's valid. It must never
// look saved: while anything is held,
//   • the page counts as having unsaved changes (navigation/unsavedChanges —
//     leaving asks "Stay / Discard changes"; refresh / tab close prompt);
//   • heldContactMessage() gives the line to show ("Email not saved").
// Written only by useAutoSave (setHeldContact); read with useHeldContact().
// ============================================================
import { useSyncExternalStore } from 'react'
import { markDirty } from '../navigation/unsavedChanges'

export type HeldContactKind = 'email' | 'phone'

const DIRTY_ID = Symbol('autosave-held-contact')
let held: HeldContactKind[] = []
const listeners = new Set<() => void>()

export const getHeldContact = (): HeldContactKind[] => held

export const setHeldContact = (next: HeldContactKind[]): void => {
  const sorted = Array.from(new Set(next)).sort() as HeldContactKind[]
  // Always re-mark: "Discard changes" clears every mark, and the same held
  // value seen again must count again.
  markDirty(DIRTY_ID, sorted.length > 0)
  if (sorted.join(',') === held.join(',')) return
  held = sorted
  listeners.forEach((l) => l())
}

// "Email not saved" / "Phone not saved" / "Email and phone not saved", or null.
export const heldContactMessage = (kinds: HeldContactKind[] = held): string | null => {
  if (!kinds.length) return null
  const has = (k: HeldContactKind): boolean => kinds.includes(k)
  return has('email') && has('phone') ? 'Email and phone not saved' : has('email') ? 'Email not saved' : 'Phone not saved'
}

export const useHeldContact = (): HeldContactKind[] =>
  useSyncExternalStore(
    (l) => { listeners.add(l); return () => { listeners.delete(l) } },
    () => held,
  )
